import React, { useEffect, useId, useRef, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import { useLocation, useParams } from 'react-router-dom';
import { cloneDeep } from 'lodash';

import type { Evento, ThreadLista } from '../api/types';
import { search_problemi_string } from '../api';
import { displayEvento } from '../utils';
import { useCategoria, useCategoriaEvento, useEvento, useLogin } from '../reducers';
import { CategoriaSelector, CategoriaEventoSelector, FormGroupBootstrap } from './';
import { empty_initial_values, filtra, FormValues } from '../utils/problemFilters';
import './FiltriProblemi.scss';

export interface LockedProblemFilter {
  field: 'categorie' | 'categorieevento';
  id: number;
  label: string;
}
interface Props {
  threads: ThreadLista[];
  lockedFilter?: LockedProblemFilter;
  children: (filtered: ThreadLista[]) => React.ReactNode;
}

const FiltriProblemi = ({ threads, lockedFilter, children }: Props) => {
  const id = useId();
  const initial_values = cloneDeep(empty_initial_values);
  const [filterValues, setFilterValues] = useState(initial_values);
  const [search, setSearch] = useState<{
    query: string; ids: number[] | null; loading: boolean; error: string | null;
  }>({ query: '', ids: null, loading: false, error: null });
  const searchRequest = useRef(0);
  const [mostraFiltri, setMostraFiltri] = useState(false);
  const categorie = useCategoria({});
  const categorieevento = useCategoriaEvento({});
  const eventi = useEvento({});
  const login = useLogin();
  const params = useParams();
  const location = useLocation();
  useEffect(() => () => { searchRequest.current += 1; }, []);

  const applyFilters = async (values: FormValues, reuseSearch = false) => {
    const next: FormValues = {
      ...values,
      categorie: [...values.categorie],
      categorieevento: values.categorieevento.map(Number),
      eventi: values.solo_non_assegnati ? [] : [...values.eventi],
      ricerca_diretta: values.ricerca_diretta.trim(),
    };
    const request = ++searchRequest.current;
    setFilterValues(next);
    if (!next.ricerca_diretta) {
      setSearch({ query: '', ids: null, loading: false, error: null });
      return;
    }
    if (reuseSearch && search.query === next.ricerca_diretta && !search.loading && !search.error) return;
    setSearch({ query: next.ricerca_diretta, ids: [], loading: true, error: null });
    try {
      const results = await search_problemi_string(next.ricerca_diretta);
      if (request === searchRequest.current) {
        setSearch({ query: next.ricerca_diretta, ids: results.map(result => result[0]), loading: false, error: null });
      }
    } catch {
      if (request === searchRequest.current) {
        setSearch({
          query: next.ricerca_diretta, ids: [], loading: false,
          error: 'Ricerca non riuscita. Riprova premendo Cerca.'
        });
      }
    }
  };

  const chips: { key: string; label: string; locked?: boolean; patch?: Partial<FormValues> }[] = [];
  if (lockedFilter?.field === 'categorie') {
    chips.push({ key: 'page-category', label: lockedFilter.label, locked: true });
  } else {
    filterValues.categorie.forEach(id => chips.push({
      key: `category-${id}`,
      label: categorie.find(item => item.id === id)?.nome || `Categoria ${id}`,
      patch: { categorie: filterValues.categorie.filter(value => value !== id) }
    }));
  }
  if (lockedFilter?.field === 'categorieevento') {
    chips.push({ key: 'page-event-category', label: lockedFilter.label, locked: true });
  } else {
    filterValues.categorieevento.forEach(id => chips.push({
      key: `event-category-${id}`,
      label: categorieevento.find(item => item.id === Number(id))?.nome || `Categoria evento ${id}`,
      patch: { categorieevento: filterValues.categorieevento.filter(value => value !== id) }
    }));
  }
  filterValues.eventi.forEach(id => chips.push({
    key: `event-${id}`, label: displayEvento(Number(id)),
    patch: { eventi: filterValues.eventi.filter(value => value !== id) }
  }));
  if (filterValues.solo_non_assegnati) chips.push({ key: 'unused', label: 'Non utilizzati', patch: { solo_non_assegnati: false } });
  if (!filterValues.mostra_scartati_duplicati) chips.push({ key: 'excluded', label: 'Scartati e duplicati nascosti', patch: { mostra_scartati_duplicati: true } });
  if (filterValues.ricerca_diretta) chips.push({ key: 'search', label: `Ricerca: ${filterValues.ricerca_diretta}`, patch: { ricerca_diretta: '' } });

  const effectiveValues = lockedFilter ? { ...filterValues, [lockedFilter.field]: [lockedFilter.id] } : filterValues;
  const lista_filtrata = filtra(login, params, location, effectiveValues, threads, null, 1, search.ids);

  return <div className="problem-search-filters">
      <Formik initialValues={initial_values} onSubmit={(values, actions) => {
        if (values.solo_non_assegnati) void actions.setFieldValue('eventi', [], false);
        return applyFilters(values);
      }}>
        {({ values, isSubmitting, setValues, resetForm, setSubmitting }) => {
          const clearFilters = () => {
            const cleared: FormValues = { ...cloneDeep(empty_initial_values), solo_non_assegnati: false, mostra_scartati_duplicati: true };
            resetForm({ values: cleared });
            setSubmitting(false);
            void applyFilters(cleared);
          };
          return (
            <Form className="problem-list-controls mb-3" aria-label="Ricerca e filtri problemi">
              <div className="problem-list-toolbar">
                <div className="input-group problem-list-search">
                  <div className="input-group-prepend"><span className="input-group-text"><i className="fa fa-search" aria-hidden="true" /></span></div>
                  <label className="sr-only" htmlFor={`${id}-search`}>Cerca nei problemi</label>
                  <Field id={`${id}-search`} name="ricerca_diretta" type="search" className="form-control" placeholder="Cerca nel testo dei problemi…" />
                  <div className="input-group-append"><button type="submit" className="btn btn-primary" disabled={isSubmitting || search.loading}>Cerca</button></div>
                </div>
                <button type="button" className="btn btn-outline-secondary" aria-expanded={mostraFiltri}
                  aria-controls={`${id}-filters`} onClick={() => setMostraFiltri(open => !open)}>
                  <i className="fa fa-filter mr-2" aria-hidden="true" />Filtri <span className="badge badge-light ml-1">{chips.length}</span>
                </button>
              </div>
              <div className="problem-list-applied" aria-label="Filtri applicati">
                {chips.map(chip => chip.locked ? <span key={chip.key} className="problem-list-chip problem-list-chip-locked" title="Filtro impostato dalla pagina corrente">
                  <i className="fa fa-lock" aria-hidden="true" />{chip.label}<span className="sr-only"> (filtro della pagina)</span>
                </span> : <button key={chip.key} type="button" className="problem-list-chip" aria-label={`Rimuovi filtro ${chip.label}`}
                  onClick={() => {
                    setSubmitting(false);
                    void setValues({ ...values, ...chip.patch }, false);
                    void applyFilters({ ...filterValues, ...chip.patch }, true);
                  }}>{chip.label}<i className="fa fa-times" aria-hidden="true" /></button>)}
                {chips.some(chip => !chip.locked) && <button type="button" className="btn btn-link btn-sm" onClick={clearFilters}>Rimuovi filtri</button>}
              </div>
              <div id={`${id}-filters`} className="problem-list-filter-panel" hidden={!mostraFiltri}>
                <div className="row">
                  <div className="col-12 col-lg-8">
                    <section className="problem-list-filter-section" aria-labelledby={`${id}-topics`}>
                      <h2 className="h6" id={`${id}-topics`}>Argomenti e destinazione</h2>
                      {lockedFilter?.field !== 'categorie' && <CategoriaSelector name="categorie" help_text="Il problema deve appartenere a tutte le categorie selezionate." />}
                      {lockedFilter?.field !== 'categorieevento' && <CategoriaEventoSelector name="categorieevento" displayName="Categorie evento"
                        help_text="Il problema deve essere proposto per almeno una delle categorie evento selezionate." />}
                      {lockedFilter && <p className="small text-muted mb-0">Il filtro della pagina è indicato dal lucchetto sopra.</p>}
                    </section>
                  </div>
                  <div className="col-12 col-lg-4">
                    <section className="problem-list-filter-section" aria-labelledby={`${id}-usage`}>
                      <h2 className="h6" id={`${id}-usage`}>Utilizzo del problema</h2>
                      <FormGroupBootstrap name="solo_non_assegnati" type="checkbox" displayName="Solo non assegnati" help_text="Solo problemi non ancora utilizzati in un evento." />
                      {!values.solo_non_assegnati && <FormGroupBootstrap name="eventi" type="select-multiple" displayName="Evento assegnato"
                        choices={eventi} displayChoice={(item: Evento) => displayEvento(item.id)} help_text="Almeno uno degli eventi selezionati." />}
                    </section>
                    <section className="problem-list-filter-section" aria-labelledby={`${id}-visibility`}>
                      <h2 className="h6" id={`${id}-visibility`}>Visibilità</h2>
                      <FormGroupBootstrap name="mostra_scartati_duplicati" type="checkbox" displayName="Includi scartati e duplicati" help_text="Mostra anche i problemi esclusi dalla selezione." />
                    </section>
                  </div>
                </div>
                <div className="d-flex flex-wrap justify-content-end problem-list-filter-actions">
                  <button type="button" className="btn btn-outline-secondary mr-2" onClick={() => {
                    const defaults = cloneDeep(empty_initial_values);
                    resetForm({ values: defaults });
                    setSubmitting(false);
                    void applyFilters(defaults);
                  }}>Ripristina</button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting || search.loading}>Applica filtri</button>
                </div>
              </div>
              {search.error && <div className="alert alert-danger mb-0 mt-3" role="alert">{search.error}</div>}
            </Form>
          );
        }}
      </Formik>
      <p className="text-muted mb-2" role="status" aria-live="polite">
        {search.loading ? 'Ricerca in corso…' : `${lista_filtrata.length} problemi trovati`}
      </p>
    {children(lista_filtrata)}
  </div>;
};

export default FiltriProblemi;
